import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import dayjs from 'dayjs'
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputRef,
  Select,
  Spin,
  Switch,
  Tooltip,
  Typography,
  message
} from 'antd'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import { TVariantMode } from 'models/app'
import { sellerApi } from 'api/seller'
import { ISeller } from 'models/seller'
import { IParsedCategory } from 'models/product'
import { productApi } from 'api/product'
import classNames from 'classnames'
import { MenuInfo } from 'rc-menu/lib/interface'
import {
  EDiscountChargeType,
  EDiscountType,
  EPromoStatus,
  EPromoTargetType,
  ICreatePromo,
  IHelperProductsResponse
} from 'models/promoCodes'
import { promoCodesApi } from 'api/promoCodes'
import { showServerError } from 'shared/utils/showServerError'
import {
  HIDE_INACTIVE_SELLERS_LABEL,
  isActiveSellerWithProducts
} from 'shared/utils/sellerFilters'
import { normalizeDigits } from 'shared/utils/normalizeDigits'
import { highlightText } from 'shared/utils/highlightSearchText'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import Table, { ColumnsType, ColumnType } from 'antd/es/table'
import { IMAGES_URL } from 'shared/constants/urls'
import noProductImage from 'assets/images/no-image-product.png'
import {
  DeleteOutlined,
  FilterFilled,
  PlusOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { appSelectors } from 'store/app'
import { ERoles } from 'models/auth'
import { useNavigate } from 'react-router-dom'
import { showMessage } from 'shared/utils/showMessage'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { MobileFilterTitle } from 'components/ui/MobileFilterTitle/MobileFilterTitle'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import {
  MAX_PROMO_TIERS,
  getPromoCodesStatusItems,
  promoCodesStatuses
} from 'shared/constants/promoCodes'
import { PromoCodeOrders } from '../PromoCodeOrders/PromoCodeOrders'
import styles from './PromoCodesForm.module.scss'

interface IPromoCodesFormProps {
  sellerId?: string
  promoCodeId?: string
  mode: TVariantMode
}

interface IPromoTierFormValues {
  minOrderSum: string | number
  discount: string | number
}

interface IFormValues {
  isGlobalPromoCode: boolean
  isDiscountChargedToSeller: boolean
  visibilityOnSellerPage: boolean
  code: string
  dates: [Date, Date]
  usageLimitUser: string | number
  usageLimitTotal: string | number
  discount: string | number
  minOrderSum: string | number
  tiers?: IPromoTierFormValues[]
  isFixedDiscount: boolean
  description: string
  sellersIds: string[]
  isFirstOrderOnly: boolean
}

export const PromoCodesForm: React.FC<IPromoCodesFormProps> = ({
  sellerId,
  mode,
  promoCodeId
}) => {
  const isMobile = useIsMobile()

  const role = useAppSelector(appSelectors.selectRole)
  const isSuperAdmin = useMemo(() => role === ERoles.ADMIN, [role])

  const userSellerId = useAppSelector(appSelectors.selectSellerId)

  const [form] = Form.useForm<IFormValues>()
  const [sellers, setSellers] = useState<ISeller[]>([])
  const [loadingSellers, setLoadingSellers] = useState(
    isSuperAdmin && !sellerId
  )
  const [hideInactiveSellers, setHideInactiveSellers] = useState(true)

  const [categories, setCategories] = useState<IParsedCategory[]>([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [selectedSubcategoriesIds, setSelectedSubcategoriesIds] = useState<
    string[]
  >([])
  const [subcategoriesSearch, setSubcategoriesSearch] = useState<string>('')

  const [products, setProducts] = useState<IHelperProductsResponse['items']>([])
  const [loadingProducts, setLoadingProducts] = useState(true)

  const [promoCodeLoaded, setPromoCodeLoaded] = useState(mode === 'create')

  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 10

  const [productNameSearch, setProductNameSearch] = useState('')
  const productNameSearchRef = useRef<InputRef>(null)

  const [sellerSearch, setSellerSearch] = useState<string | undefined>(
    undefined
  )
  const [selectedSellerId, setSelectedSellerId] = useState<string | null>(null)

  const [selectedProductsIds, setSelectedProductsIds] = useState<string[]>([])

  const [promoCodeStatus, setPromoCodeStatus] = useState<EPromoStatus | null>(
    null
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSelectingAll, setIsSelectingAll] = useState(false)

  const getProducts = async (
    skip: number,
    data?: {
      productName?: string
      sellerId?: string
    }
  ) => {
    try {
      setLoadingProducts(true)
      const response = await promoCodesApi.getProducts({
        skip: String(skip),
        limit: String(pageSize),
        search: data?.productName ?? productNameSearch ?? undefined,
        sort_seller_id:
          data?.sellerId ??
          selectedSellerId ??
          sellerId ??
          (isSuperAdmin ? undefined : userSellerId ?? undefined)
      })
      setTotal(response.data.count)
      setProducts(response.data.items)
    } catch (e) {
      showServerError(e)
    } finally {
      setLoadingProducts(false)
    }
  }

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const { data } = await sellerApi.getSearchSellers(true)
        if (Array.isArray(data)) {
          setSellers(data)
        }
      } catch {
        message.error('Не удалось загрузить список продавцов')
      } finally {
        setLoadingSellers(false)
      }
    }
    const fetchCategories = async () => {
      try {
        const { data } = await productApi.getCategories({ parsed: 'true' })
        if (Array.isArray(data)) {
          setCategories(data as IParsedCategory[])
        }
        if (data && data[0]) {
          setSelectedCategoryId((data[0] as IParsedCategory).value)
        }
      } catch {
        message.error('Не удалось загрузить список категорий')
      } finally {
        setLoadingCategories(false)
      }
    }

    if (isSuperAdmin && !sellerId) {
      fetchSellers()
    }
    fetchCategories()
    getProducts(0)
  }, [])

  useEffect(() => {
    if (mode === 'edit' && promoCodeId) {
      const fetchPromoCode = async () => {
        try {
          const { data } = await promoCodesApi.getPromoCode(promoCodeId)
          form.setFields([
            {
              name: 'isGlobalPromoCode',
              value: data.target_type === EPromoTargetType.GLOBAL
            },
            {
              name: 'isDiscountChargedToSeller',
              value: data.discount_charge_type === EDiscountChargeType.SELLER
            },
            {
              name: 'visibilityOnSellerPage',
              value: data.visability_on_seller_page
            },
            { name: 'code', value: data.code },
            {
              name: 'dates',
              value: [dayjs(data.start_date), dayjs(data.end_date)]
            },
            { name: 'usageLimitUser', value: data.usage_limit_user },
            { name: 'usageLimitTotal', value: data.usage_limit_total },
            { name: 'discount', value: data.discount_value },
            { name: 'minOrderSum', value: data.min_order_sum },
            {
              name: 'tiers',
              value: (data.tiers ?? []).map((tier) => ({
                minOrderSum: tier.min_order_sum,
                discount: tier.discount_value
              }))
            },
            {
              name: 'isFixedDiscount',
              value: data.discount_type === EDiscountType.FIXED
            },
            { name: 'isFirstOrderOnly', value: data.first_order_only },
            { name: 'description', value: data.description },
            {
              name: 'sellersIds',
              value: data.target_sellers?.map((seller) => seller._id)
            }
          ])
          if (data.target_products) {
            setSelectedProductsIds(
              data.target_products.map((product) => product)
            )
          }
          if (data.target_subcategories) {
            setSelectedSubcategoriesIds(
              data.target_subcategories.map((subcategory) => subcategory)
            )
          }
          setPromoCodeStatus(data.status)
        } catch (error) {
          showServerError(error)
        } finally {
          setPromoCodeLoaded(true)
        }
      }
      fetchPromoCode()
    }
  }, [promoCodeId])

  const onRowSelection = (selectedItemsId: React.Key[]) => {
    setSelectedProductsIds(selectedItemsId as string[])
    // выбор конкретных товаров исключает выбор по категориям — снимаем подкатегории
    if (selectedItemsId.length) {
      setSelectedSubcategoriesIds([])
    }
  }

  const onPageChange = (current: number) => {
    getProducts((current - 1) * pageSize)
    setCurrentPage(current)
  }

  const onSaveProductName = (nameSearch: string) => {
    getProducts(0, { productName: nameSearch })
    setCurrentPage(1)
  }

  const debouncedGetProductsByName = useCallback(
    debounce(onSaveProductName, DEBOUNCE_DELAY),
    [selectedSellerId]
  )

  // выбрать все товары по текущему фильтру (поиск/продавец), а не только текущую страницу
  const selectAllFoundProducts = async () => {
    try {
      setIsSelectingAll(true)
      const response = await promoCodesApi.getProducts({
        skip: '0',
        limit: String(total),
        search: productNameSearch || undefined,
        sort_seller_id:
          selectedSellerId ??
          sellerId ??
          (isSuperAdmin ? undefined : userSellerId ?? undefined)
      })
      onRowSelection(response.data.items.map((item) => item._id))
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSelectingAll(false)
    }
  }

  const onSaveSeller = (sellerId: string | null) => {
    getProducts(0, { sellerId: sellerId ?? undefined })
    setCurrentPage(1)
  }

  const getTargetSellers = (values: IFormValues) => {
    if (isSuperAdmin && sellerId) {
      return [sellerId]
    }
    if (isSuperAdmin && isGlobalPromoCode) {
      return []
    }
    if (isSuperAdmin && !isGlobalPromoCode) {
      return values.sellersIds
    }
    return userSellerId ? [userSellerId] : []
  }

  const onFinish = async (values: IFormValues) => {
    if (
      !values.isGlobalPromoCode &&
      isSuperAdmin &&
      !sellerId &&
      !selectedProductsIds.length &&
      !selectedSubcategoriesIds.length &&
      !values?.sellersIds?.length
    ) {
      return showMessage(
        'error',
        'Выберите продавцов, подкатегории или товары, чтобы создать промокод, либо включите «На все товары»',
        ''
      )
    }
    if (
      !values.isGlobalPromoCode &&
      isSuperAdmin &&
      sellerId &&
      !selectedProductsIds.length &&
      !selectedSubcategoriesIds.length
    ) {
      return showMessage(
        'error',
        'Выберите подкатегории или товары, чтобы создать промокод, либо включите «На все товары»',
        ''
      )
    }
    if (
      !values.isGlobalPromoCode &&
      !isSuperAdmin &&
      !selectedProductsIds.length &&
      !selectedSubcategoriesIds.length
    ) {
      return showMessage(
        'error',
        'Выберите подкатегории или товары, чтобы создать промокод, либо включите «На все товары»',
        ''
      )
    }
    const getNumericValue = (value?: string | number) => {
      if (typeof value === 'number') {
        return String(value)
      }
      if (typeof value === 'string') {
        return value.replace(/\D/g, '')
      }
      return ''
    }

    const usageLimitUser = getNumericValue(values.usageLimitUser)
    const usageLimitTotal = getNumericValue(values.usageLimitTotal)
    const discountValue = getNumericValue(values.discount)
    const minOrderSum = getNumericValue(values.minOrderSum)
    const visibilityOnSellerPage = Boolean(
      form.getFieldValue('visibilityOnSellerPage')
    )

    const data: ICreatePromo = {
      code: values.code,
      start_date: dayjs(values.dates[0]).startOf('day').format() as any,
      end_date: dayjs(values.dates[1]).endOf('day').format() as any,
      usage_limit_user: usageLimitUser ? Number(usageLimitUser) : undefined,
      usage_limit_total: usageLimitTotal ? Number(usageLimitTotal) : undefined,
      discount_type: values.isFixedDiscount
        ? EDiscountType.FIXED
        : EDiscountType.PERCENT,
      discount_value: Number(discountValue),
      // промокод продавца всегда компенсируется им самим; у админа — по свитчу
      // (SELLER только при определённом продавце, иначе принудительно STORE)
      discount_charge_type: !isSuperAdmin
        ? EDiscountChargeType.SELLER
        : values.isDiscountChargedToSeller && isSellerScopeSpecified
          ? EDiscountChargeType.SELLER
          : EDiscountChargeType.STORE,
      visability_on_seller_page:
        isSellerScopeSpecified || !isSuperAdmin
          ? visibilityOnSellerPage
          : undefined,
      // 0, а не undefined: иначе очищенное поле при правке оставит старый порог
      min_order_sum: Number(minOrderSum || 0),
      tiers: (values.tiers ?? []).map((tier) => ({
        min_order_sum: Number(getNumericValue(tier.minOrderSum)),
        discount_value: Number(getNumericValue(tier.discount))
      })),
      description: values.description,
      first_order_only: Boolean(values.isFirstOrderOnly),
      target_type: values.isGlobalPromoCode
        ? EPromoTargetType.GLOBAL
        : EPromoTargetType.SPECIFIC,
      target_sellers:
        selectedProductsIds.length > 0 ? [] : getTargetSellers(values),
      target_subcategories:
        selectedProductsIds.length > 0
          ? []
          : values.isGlobalPromoCode
            ? []
            : selectedSubcategoriesIds,
      target_products: values.isGlobalPromoCode ? [] : selectedProductsIds
    }
    try {
      setIsSubmitting(true)
      if (mode === 'edit' && promoCodeId) {
        await promoCodesApi.updatePromoCode(promoCodeId, data)
      } else {
        await promoCodesApi.createPromoCode(data)
      }
      message.success(
        mode === 'create'
          ? 'Промокод успешно создан'
          : 'Промокод успешно обновлен'
      )
    } catch (error) {
      showServerError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const changePromoCodeStatus = async (e: MenuInfo) => {
    try {
      message.loading('Обновление статуса')
      await promoCodesApi.changePromoCodeStatus(promoCodeId as string, {
        status: e.key as EPromoStatus
      })
      setPromoCodeStatus(e.key as EPromoStatus)
      message.destroy()
      message.success('Статус успешно сменен')
    } catch (e) {
      message.destroy()
      showServerError(e)
    }
  }

  // Функция для получения отображаемого имени продавца
  const getSellerLabel = (seller: ISeller) => {
    const s = seller as any
    const shopName = s.shop_details?.shop_name || s.shopName
    const fullName = s.full_name || `${s.name || ''} ${s.surname || ''}`.trim()
    return shopName || fullName || s._id
  }

  const targetSellerIds: string[] = Form.useWatch('sellersIds', form) ?? []

  const visibleSellers = useMemo(() => {
    if (!hideInactiveSellers) return sellers
    // уже выбранных не прячем: иначе при редактировании промокода вместо
    // названия магазина в поле останется голый id
    return sellers.filter(
      (seller) =>
        isActiveSellerWithProducts(seller) ||
        targetSellerIds.includes(seller._id)
    )
  }, [sellers, hideInactiveSellers, targetSellerIds])

  const normalizedSellers = useMemo(() => {
    return sellers.map((item) => ({
      label: item.shop_details.shop_name,
      value: item.shop_details.shop_name,
      key: item._id
    }))
  }, [sellers])

  const isFixedDiscount: boolean | undefined = Form.useWatch(
    'isFixedDiscount',
    {
      form,
      preserve: true
    }
  )

  const isGlobalPromoCode: boolean | undefined = Form.useWatch(
    'isGlobalPromoCode',
    {
      form,
      preserve: true
    }
  )

  const watchedSellersIds: string[] | undefined = Form.useWatch('sellersIds', {
    form,
    preserve: true
  })

  const isDiscountChargedToSeller: boolean | undefined = Form.useWatch(
    'isDiscountChargedToSeller',
    { form, preserve: true }
  )

  // Продавец платит комиссию с полной цены товара: у продавца — всегда,
  // у админа — когда включён свитч «Компенсируется продавцом»
  const showSellerCommissionHint =
    !isSuperAdmin || Boolean(isDiscountChargedToSeller)

  // Продавец определён: карточка продавца, собственная форма продавца или выбран в мультиселекте
  const isSellerSpecified =
    Boolean(sellerId) || !isSuperAdmin || (watchedSellersIds?.length ?? 0) > 0
  const isSellerScopeSpecified =
    !isSuperAdmin ||
    Boolean(sellerId) ||
    (!isGlobalPromoCode && (watchedSellersIds?.length ?? 0) > 0)

  // без продавца компенсация невозможна — выключаем свитч, а не только блокируем
  useEffect(() => {
    if (!isSellerScopeSpecified || !isSuperAdmin) {
      form.setFieldValue('isDiscountChargedToSeller', false)
    }
    if (!isSellerScopeSpecified && isSuperAdmin) {
      form.setFieldValue('visibilityOnSellerPage', false)
    }
  }, [form, isSellerScopeSpecified, isSuperAdmin])

  const columns: ColumnsType<IHelperProductsResponse['items'][number]> =
    useMemo(() => {
      const baseColumns: ColumnsType<IHelperProductsResponse['items'][number]> =
        [
          {
            title: 'Фото',
            dataIndex: 'photo',
            width: 76,
            render: (_, { photo }) => {
              return photo && !!photo.length ? (
                <div
                  style={{
                    width: 62,
                    height: 34,
                    backgroundImage: `url(${IMAGES_URL}${photo[0]})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              ) : (
                <img
                  src={noProductImage}
                  alt='Изображение товара'
                  width={62}
                  height={34}
                  style={{ objectFit: 'cover' }}
                />
              )
            }
          },
          {
            title: 'Наименование товара',
            dataIndex: 'name',
            render: (_, { name }) => highlightText(name ?? '', productNameSearch),
            filterDropdown: ({ close }) => {
              return (
                <div
                  className='filterWrapper'
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Input
                    ref={productNameSearchRef}
                    value={productNameSearch}
                    onChange={(e) => setProductNameSearch(e.target.value)}
                    onPressEnter={() => {
                      onSaveProductName(productNameSearch)
                      close()
                    }}
                    placeholder='Наименование товара'
                    className='filterInput'
                  />
                  <div className='filterFooterButtons'>
                    <Button
                      type='primary'
                      onClick={() => {
                        onSaveProductName(productNameSearch)
                        close()
                      }}
                      icon={<SearchOutlined />}
                      size='small'
                      className='filterButton'
                    >
                      Найти
                    </Button>
                    <Button
                      onClick={() => {
                        setProductNameSearch('')
                        onSaveProductName('')
                        close()
                      }}
                      size='small'
                      className='filterButton'
                    >
                      Очистить
                    </Button>
                    <Button
                      type='link'
                      size='small'
                      onClick={() => {
                        close()
                      }}
                    >
                      Закрыть
                    </Button>
                  </div>
                </div>
              )
            },
            onFilterDropdownOpenChange: (visible) => {
              if (visible) {
                setTimeout(() => productNameSearchRef.current?.select(), 100)
              }
            },
            filterIcon: () => (
              <SearchOutlined
                style={{
                  color: productNameSearch ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
                }}
              />
            )
          },
          {
            title: 'Цена',
            dataIndex: 'price',
            render: (_, { price }) => {
              return (
                <Typography.Text
                  onClick={(event) => event.stopPropagation()}
                  style={{ opacity: price ? 1 : 0.5 }}
                >
                  {price ? getNormalizedPrice(price) : 'Не задана'}
                </Typography.Text>
              )
            }
          }
        ]
      if (isSuperAdmin && !sellerId) {
        const [photoColumn, nameColumn, ...otherColumns] = baseColumns
        const sellerColumn: ColumnsType<
          IHelperProductsResponse['items'][number]
        >[0] = {
          title: 'Продавец',
          dataIndex: 'seller',
          // Соседней вкладкой, чтобы не потерять несохранённый промокод
          render: (_, { seller_id }) => {
            return seller_id?._id && seller_id.shop_details?.shop_name ? (
              <CardLink to={getSellerEditPath(seller_id._id)} newTab>
                {seller_id.shop_details.shop_name}
              </CardLink>
            ) : (
              <Typography.Text
                style={{ opacity: 0.5 }}
                onClick={(event) => event.stopPropagation()}
              >
                Удаленный аккаунт
              </Typography.Text>
            )
          },
          filterDropdown: ({ close }) => {
            return (
              <div
                className='filterWrapper'
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Select
                  value={sellerSearch}
                  onChange={(value) => {
                    setSellerSearch(value)
                    setSelectedSellerId('')
                  }}
                  className='filterInput filterSelect'
                  dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                  placeholder='Выберите продавца'
                  onSelect={(_, item) => setSelectedSellerId(item.key)}
                  options={normalizedSellers}
                  showSearch
                  allowClear
                />
                <div className='filterFooterButtons'>
                  <Button
                    type='primary'
                    onClick={() => {
                      onSaveSeller(selectedSellerId)
                      close()
                    }}
                    icon={<SearchOutlined />}
                    size='small'
                    className='filterButton'
                  >
                    Найти
                  </Button>
                  <Button
                    onClick={() => {
                      setSellerSearch(undefined)
                      setSelectedSellerId('')
                      onSaveSeller('')
                      close()
                    }}
                    size='small'
                    className='filterButton'
                  >
                    Очистить
                  </Button>
                  <Button
                    type='link'
                    size='small'
                    onClick={() => {
                      close()
                    }}
                  >
                    Закрыть
                  </Button>
                </div>
              </div>
            )
          },
          filterIcon: () => (
            <FilterFilled
              style={{
                color: selectedSellerId ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
              }}
            />
          )
        }
        return [photoColumn, nameColumn, sellerColumn, ...otherColumns]
      }
      return baseColumns
    }, [
      products,
      normalizedSellers,
      productNameSearch,
      sellerSearch,
      selectedSellerId,
      isSuperAdmin,
      sellerId
    ])

  // Рендеры мобильных колонок берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<IHelperProductsResponse['items'][number]> =
    useMemo(() => {
      const findColumn = (dataIndex: string) =>
        columns.find(
          (
            column
          ): column is ColumnType<IHelperProductsResponse['items'][number]> =>
            'dataIndex' in column && column.dataIndex === dataIndex
        )

      const photoColumn = findColumn('photo')
      const nameColumn = findColumn('name')
      const sellerColumn = findColumn('seller')
      const priceColumn = findColumn('price')

      return [
        {
          title: (
            <MobileFilterTitle
              title='Товар'
              columns={[nameColumn, sellerColumn]}
              isActive={Boolean(productNameSearch || sellerSearch)}
            />
          ),
          dataIndex: 'name',
          width: '62%',
          render: (value, record, index) => (
            <div className={styles.mobileProductCell}>
              <div className={styles.mobilePhoto}>
                {photoColumn?.render?.(value, record, index) as React.ReactNode}
              </div>
              <div className={styles.mobileProductInfo}>
                {nameColumn?.render?.(value, record, index) as React.ReactNode}
                {sellerColumn && (
                  <span className={styles.mobileSubText}>
                    {
                      sellerColumn.render?.(
                        value,
                        record,
                        index
                      ) as React.ReactNode
                    }
                  </span>
                )}
              </div>
            </div>
          )
        },
        {
          ...priceColumn,
          width: '38%'
        }
      ]
    }, [columns, productNameSearch, sellerSearch])

  const isLoading = loadingSellers || loadingCategories || !promoCodeLoaded

  if (isLoading) {
    return (
      <div
        style={{
          height: '60vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Spin size='large' />
      </div>
    )
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <div className={styles.header}>
        <MainTitle
          text={
            mode === 'create'
              ? 'Создание промокода'
              : `Промокод ${form.getFieldValue('code')}`
          }
          style={{ marginBottom: 0 }}
          isGoBack
        />
        {promoCodeStatus && (
          <StatusDropdown
            items={getPromoCodesStatusItems(promoCodeStatus)}
            label={promoCodesStatuses[promoCodeStatus].label}
            color={promoCodesStatuses[promoCodeStatus].color}
            onClick={(e) => changePromoCodeStatus(e)}
          />
        )}
      </div>
      {mode === 'edit' && promoCodeId && (
        <PromoCodeOrders promoCodeId={promoCodeId} />
      )}
      <Form
        form={form}
        name='promoCode'
        size='large'
        onFinish={onFinish}
        initialValues={{
          isDiscountChargedToSeller: false,
          visibilityOnSellerPage: false,
          isFirstOrderOnly: false
        }}
      >
        <div className={styles.formHeader}>
          <Typography.Title level={4} className={styles.title}>
            Основные настройки
          </Typography.Title>
        </div>
        <div className={styles.row}>
          <Form.Item
            label='Промокод инфо'
            name='code'
            rules={[
              { required: true, message: 'Введите код промокода' },
              {
                pattern: /^[A-Z0-9]+$/,
                message: 'Только английские заглавные буквы и цифры'
              }
            ]}
            normalize={(value) => value?.toUpperCase()?.replace(/\s/g, '')}
            style={{ marginBottom: 0, flexGrow: 1 }}
          >
            <Input placeholder='Код промокода' />
          </Form.Item>
          <Form.Item
            name='dates'
            rules={[{ required: true, message: 'Выберите период действия' }]}
            style={{ marginBottom: 0, flexGrow: 1 }}
          >
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              format='DD.MM.YYYY'
              placeholder={['Начало', 'Конец']}
            />
          </Form.Item>
        </div>

        <div className={styles.row} style={{ alignItems: 'center' }}>
          <Form.Item
            label='Количество применений (Per user)'
            name='usageLimitUser'
            style={{ marginBottom: 0 }}
            rules={[
              {
                required: true,
                message: 'Укажите количество применений (Per user)'
              }
            ]}
            normalize={normalizeDigits(5)}
          >
            <Input placeholder='0' />
          </Form.Item>
          <Typography.Text style={{ color: '#1677FF' }}>
            0 = Без ограничения
          </Typography.Text>
        </div>
        <div className={styles.row} style={{ alignItems: 'center' }}>
          <Form.Item
            label='Количество применений (За все время)'
            name='usageLimitTotal'
            style={{ marginBottom: 0 }}
            rules={[
              {
                required: true,
                message: 'Укажите количество применений (За все время)'
              }
            ]}
            normalize={normalizeDigits(5)}
          >
            <Input placeholder='0' />
          </Form.Item>
          <Typography.Text style={{ color: '#1677FF' }}>
            0 = Без ограничения
          </Typography.Text>
        </div>

        <div className={styles.row}>
          <Form.Item
            label='Скидка'
            name='discount'
            rules={[
              {
                required: true,
                message: `Введите ${
                  isFixedDiscount ? 'размер' : 'процент'
                } скидки`
              },
              {
                validator: (_, value) => {
                  if (isFixedDiscount || value === undefined || value === '') {
                    return Promise.resolve()
                  }
                  const percent = Number(value)
                  return percent >= 1 && percent <= 99
                    ? Promise.resolve()
                    : Promise.reject(
                      new Error('Процент скидки — целое число от 1 до 99')
                    )
                }
              }
            ]}
            normalize={normalizeDigits(isFixedDiscount ? 11 : 2)}
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder={isFixedDiscount ? 'Размер скидки' : 'Процент скидки'}
              suffix={isFixedDiscount ? '₽' : '%'}
            />
          </Form.Item>
          <Form.Item
            name='isFixedDiscount'
            label='Абсолютная скидка:'
            valuePropName='checked'
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
        </div>

        {showSellerCommissionHint && (
          <Alert
            type='warning'
            showIcon
            style={{ marginBottom: 16, width: 'fit-content', maxWidth: '100%' }}
            message='Комиссия площадки удерживается с полной стоимости товара'
          />
        )}

        <div className={styles.row}>
          <Form.Item
            label='Минимальная стоимость для срабатывания'
            name='minOrderSum'
            style={{ marginBottom: 0 }}
            normalize={normalizeDigits(11)}
          >
            <Input placeholder='0' suffix='₽' />
          </Form.Item>
        </div>

        <Form.List name='tiers'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => {
                const previousSumPath =
                  name === 0 ? 'minOrderSum' : ['tiers', name - 1, 'minOrderSum']
                const previousDiscountPath =
                  name === 0 ? 'discount' : ['tiers', name - 1, 'discount']
                return (
                  <div key={key} className={styles.row}>
                    <Form.Item
                      label='Скидка'
                      name={[name, 'discount']}
                      dependencies={[previousDiscountPath]}
                      normalize={normalizeDigits(isFixedDiscount ? 11 : 2)}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Введите скидку' },
                        ({ getFieldValue }) => ({
                          validator: (_, value) => {
                            const previous = Number(
                              getFieldValue(previousDiscountPath) || 0
                            )
                            return !value || Number(value) > previous
                              ? Promise.resolve()
                              : Promise.reject(
                                new Error(
                                  'Должна быть больше скидки предыдущего условия'
                                )
                              )
                          }
                        })
                      ]}
                    >
                      <Input
                        placeholder={
                          isFixedDiscount ? 'Размер скидки' : 'Процент скидки'
                        }
                        suffix={isFixedDiscount ? '₽' : '%'}
                      />
                    </Form.Item>
                    <Form.Item
                      label='Сумма заказа от'
                      name={[name, 'minOrderSum']}
                      dependencies={[previousSumPath]}
                      normalize={normalizeDigits(11)}
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: 'Введите сумму' },
                        ({ getFieldValue }) => ({
                          validator: (_, value) => {
                            const previous = Number(
                              getFieldValue(previousSumPath) || 0
                            )
                            return !value || Number(value) > previous
                              ? Promise.resolve()
                              : Promise.reject(
                                new Error(
                                  `Должна быть больше ${previous} ₽ — суммы предыдущего условия`
                                )
                              )
                          }
                        })
                      ]}
                    >
                      <Input placeholder='Сумма' suffix='₽' />
                    </Form.Item>
                    <Button
                      type='text'
                      danger
                      icon={<DeleteOutlined />}
                      aria-label='Удалить условие'
                      onClick={() => remove(name)}
                    />
                  </div>
                )
              })}
              <div className={styles.row} style={{ alignItems: 'center' }}>
                {fields.length < MAX_PROMO_TIERS && (
                  <Button icon={<PlusOutlined />} onClick={() => add()}>
                    Добавить условие
                  </Button>
                )}
                <Tooltip title='Чем больше сумма заказа, тем больше скидка. Срабатывает одно условие — с наибольшей достигнутой суммой.'>
                  <QuestionCircleOutlined
                    style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }}
                  />
                </Tooltip>
              </div>
            </>
          )}
        </Form.List>

        <Form.Item name='description' label='Описание (видно пользователям)'>
          <Input.TextArea
            placeholder='Введите описание'
            autoSize={{ minRows: 2, maxRows: 100 }}
          />
        </Form.Item>
        {/* заголовок области действия со свитчем виден всегда — иначе «На все товары» не выключить */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            margin: '32px 0'
          }}
        >
          <Typography.Title level={4} style={{ margin: 0 }}>
            Область действия:
          </Typography.Title>
          <Form.Item
            name='isGlobalPromoCode'
            valuePropName='checked'
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
          <Typography.Text>На все товары</Typography.Text>
          <Form.Item
            name='isFirstOrderOnly'
            valuePropName='checked'
            style={{ marginBottom: 0, marginLeft: 12 }}
          >
            <Switch />
          </Form.Item>
          <Typography.Text>Только первый заказ (новый покупатель)</Typography.Text>
          {/* при неактивном блоке категорий сохранение доступно из этого ряда, кнопка у правого края */}
          {isGlobalPromoCode && (
            <Form.Item style={{ marginBottom: 0, marginLeft: 'auto' }}>
              <Button
                type='primary'
                htmlType='submit'
                icon={<SaveOutlined />}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Сохранить
              </Button>
            </Form.Item>
          )}
        </div>
        {/* продавцы и компенсация неактивны при «На все товары»;
            у продавца блока нет — компенсация для его промокодов всегда SELLER (см. onFinish) */}
        {isSuperAdmin && (
          <div
            className={classNames({ [styles.scopeDisabled]: isGlobalPromoCode })}
            style={{ marginBottom: 24 }}
          >
            {isSuperAdmin && !sellerId && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 8
                }}
              >
                <p className={styles.columnTitle} style={{ padding: 0, margin: 0 }}>
                  Продавцы
                </p>
                <Checkbox
                  checked={hideInactiveSellers}
                  onChange={(e) => setHideInactiveSellers(e.target.checked)}
                >
                  <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                    {HIDE_INACTIVE_SELLERS_LABEL}
                  </Typography.Text>
                </Checkbox>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16
              }}
            >
              {isSuperAdmin && !sellerId && (
                <Form.Item
                  name='sellersIds'
                  style={{ marginBottom: 0, flexGrow: 1, minWidth: 260 }}
                >
                  <Select
                    mode='multiple'
                    placeholder='Выберите продавцов'
                    loading={loadingSellers}
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={visibleSellers.map((seller) => ({
                      value: seller._id,
                      label: getSellerLabel(seller)
                    }))}
                    allowClear
                  />
                </Form.Item>
              )}
              {isSuperAdmin && isSellerScopeSpecified && (
                <Form.Item
                  name='isDiscountChargedToSeller'
                  label='Компенсируется продавцом:'
                  valuePropName='checked'
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
              )}
              {isSellerScopeSpecified && (
                <Form.Item
                  name='visibilityOnSellerPage'
                  label='Отображать промокод на странице продавца:'
                  valuePropName='checked'
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
              )}
            </div>
          </div>
        )}
        {/* при «На все товары» блок гаснет (scopeDisabled) — страница не скачет по высоте */}
        {/* слева поиск товаров + стек «Категории»/«Подкатегории», справа таблица товаров */}
        <div className={styles.columnsWrapper}>
          <div className={styles.categoriesStack}>
            <div
              className={classNames(styles.stackContent, {
                [styles.scopeDisabled]: isGlobalPromoCode
              })}
            >
              <div>
                <p className={styles.columnTitle} style={{ padding: 0 }}>
                    Товары
                </p>
                <Input.Search
                  allowClear
                  value={productNameSearch}
                  placeholder='Поиск товаров'
                  onChange={(e) => {
                    setProductNameSearch(e.target.value)
                    debouncedGetProductsByName(e.target.value)
                  }}
                  style={{ margin: '8px 0' }}
                />
                <Typography.Text strong style={{ display: 'block' }}>
                    Выбрано товаров: {selectedProductsIds.length}
                </Typography.Text>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button
                    size='small'
                    loading={isSelectingAll}
                    disabled={!total}
                    onClick={selectAllFoundProducts}
                  >
                      Все найденные
                  </Button>
                  <Button
                    size='small'
                    onClick={() => setSelectedProductsIds([])}
                  >
                      Отменить выбор
                  </Button>
                </div>
              </div>
              {categories.length > 0 && (
                <>
                  <div className={styles.categoryColumn}>
                    <p className={styles.columnTitle}>Категории</p>
                    <div className={styles.categoryList}>
                      {categories.map((category) => (
                        <div
                          key={category.value}
                          className={styles.categoryItem}
                          style={{
                            backgroundColor:
                              selectedCategoryId === category.value
                                ? '#1677FF'
                                : 'transparent',
                            color:
                              selectedCategoryId === category.value
                                ? '#FFFFFF'
                                : 'rgba(0, 0, 0, 0.88)'
                          }}
                          onClick={() => setSelectedCategoryId(category.value)}
                        >
                          {category.title}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={styles.subcategoriesColumn}>
                    <div className={styles.subcategoriesColumnHeader}>
                      <Checkbox
                        checked={(() => {
                          const currentCategory = categories.find(
                            (category) => category.value === selectedCategoryId
                          )
                          if (!currentCategory?.children.length) return false
                          return currentCategory.children.every(
                            (subcategory) =>
                              selectedSubcategoriesIds.includes(
                                subcategory.value
                              )
                          )
                        })()}
                        onChange={() => {
                          const currentCategory = categories.find(
                            (category) => category.value === selectedCategoryId
                          )
                          if (!currentCategory) return

                          const currentSubcategoryIds =
                            currentCategory.children.map(
                              (subcategory) => subcategory.value
                            )
                          const allSelected = currentCategory.children.every(
                            (subcategory) =>
                              selectedSubcategoriesIds.includes(
                                subcategory.value
                              )
                          )

                          if (allSelected) {
                            // Убираем все подкатегории текущей категории
                            setSelectedSubcategoriesIds(
                              selectedSubcategoriesIds.filter(
                                (id) => !currentSubcategoryIds.includes(id)
                              )
                            )
                          } else {
                            // Добавляем все подкатегории текущей категории
                            setSelectedSubcategoriesIds([
                              ...selectedSubcategoriesIds.filter(
                                (id) => !currentSubcategoryIds.includes(id)
                              ),
                              ...currentSubcategoryIds
                            ])
                            // выбор подкатегорий исключает выбор конкретных товаров
                            setSelectedProductsIds([])
                          }
                        }}
                      />
                      <p
                        className={classNames(
                          styles.columnTitle,
                          styles.subcategoriesColumnTitle
                        )}
                      >
                        Подкатегории
                      </p>
                    </div>
                    <Input.Search
                      allowClear
                      value={subcategoriesSearch}
                      placeholder='Поиск'
                      size='small'
                      onChange={(e) => setSubcategoriesSearch(e.target.value)}
                      className={styles.search}
                    />
                    <div className={styles.subcategoriesList}>
                      {categories
                        .find(
                          (category) => category.value === selectedCategoryId
                        )
                        ?.children.filter((subcategory) =>
                          subcategory.title
                            .toLocaleLowerCase()
                            .includes(subcategoriesSearch.toLocaleLowerCase())
                        )
                        .map((subcategory) => (
                          <Checkbox
                            key={subcategory.value}
                            className={styles.subcategoryItem}
                            value={subcategory.number}
                            checked={selectedSubcategoriesIds.includes(
                              subcategory.value
                            )}
                            onChange={(e) => {
                              setSelectedSubcategoriesIds(
                                e.target.checked
                                  ? [
                                    ...selectedSubcategoriesIds,
                                    subcategory.value
                                  ]
                                  : selectedSubcategoriesIds.filter(
                                    (id) => id !== subcategory.value
                                  )
                              )
                              // выбор подкатегории исключает выбор конкретных товаров
                              if (e.target.checked) {
                                setSelectedProductsIds([])
                              }
                            }}
                          >
                            {highlightText(
                              subcategory.title,
                              subcategoriesSearch
                            )}
                          </Checkbox>
                        ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <Form.Item
              style={{ marginBottom: 0 }}
              className={classNames({
                [styles.scopeHidden]: isGlobalPromoCode
              })}
            >
              <Button
                type='primary'
                htmlType='submit'
                icon={<SaveOutlined />}
                loading={isSubmitting}
                disabled={isSubmitting}
                block
              >
                    Сохранить
              </Button>
            </Form.Item>
          </div>
          <div
            className={classNames(styles.productsTableWrapper, {
              [styles.scopeDisabled]: isGlobalPromoCode
            })}
          >
            <Table
              scroll={isMobile ? undefined : { x: 600 }}
              size={isMobile ? 'small' : undefined}
              tableLayout={isMobile ? 'fixed' : undefined}
              columns={isMobile ? mobileColumns : columns}
              dataSource={products}
              loading={loadingProducts}
              rowKey={(el) => el._id}
              rowSelection={{
                selectedRowKeys: selectedProductsIds,
                onChange: onRowSelection,
                // выбор не слетает при перелистывании страниц
                preserveSelectedRowKeys: true
              }}
              rowClassName='tableRow'
              pagination={{
                current: currentPage,
                onChange: onPageChange,
                pageSize,
                total,
                showSizeChanger: false
              }}
            />
          </div>
        </div>


      </Form>
    </div>
  )
}
